// OnboardingDetailPage-part5.tsx
// PART 5: StepCard and Helper Components

import React from 'react';
import {
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import apiClient from '../services/apiClient';

// ============================================================================
// STEPCARD COMPONENT
// ============================================================================

export interface StepCardProps {
  stepNumber: number;
  title: string;
  icon: any;
  completed: boolean;
  status: string;
  adminReviewStatus?: string;
  onboardingStatus?: string;
  adjudication?: string;
  expanded: boolean;
  onToggle: () => void;
  onApprove: () => void;
  onReject: () => void;
  children?: React.ReactNode;
  hideStepActions?: boolean;
}

export const StepCard: React.FC<StepCardProps> = ({
  stepNumber,
  title,
  icon: Icon,
  completed,
  status,
  adminReviewStatus,
  onboardingStatus,
  adjudication,
  expanded,
  onToggle,
  onApprove,
  onReject,
  children,
  hideStepActions = false
}) => {
  const isRejected = adminReviewStatus === 'rejected';
  const isPendingReview = adminReviewStatus === 'pending_review';
  const isApproved = completed && adminReviewStatus === 'approved';
  
  return (
    <div className={`step-card ${expanded ? 'expanded' : ''} ${completed ? 'completed' : ''} ${isRejected ? 'rejected' : ''}`}>
      <div className="step-header" onClick={onToggle}>
        <div className="step-left">
          <div className={`step-number ${isRejected ? 'rejected' : completed ? 'completed' : ''}`}>
            {isRejected ? <XCircle size={20} /> : completed ? <CheckCircle size={20} /> : stepNumber}
          </div>
          <div className="step-icon">
            <Icon size={20} />
          </div>
          <div className="step-title-section">
            <h3>{title}</h3>
            <div className="step-badges">
              <span className="badge badge-secondary">Step {stepNumber}/8</span>
              {isApproved ? (
                <span className="badge badge-success">
                  <CheckCircle size={14} /> Approved
                </span>
              ) : isRejected ? (
                <span className="badge badge-danger">
                  <XCircle size={14} /> Rejected
                </span>
              ) : isPendingReview ? (
                <span className="badge badge-warning">
                  <Clock size={14} /> Pending Review
                </span>
              ) : (
                <span className="badge badge-pending">
                  <Clock size={14} /> {status || 'Pending'}
                </span>
              )}
              {adjudication && (
                <span className={`badge badge-${adjudication === 'pass' ? 'success' : adjudication === 'fail' ? 'danger' : 'warning'}`}>
                  {adjudication}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="step-right">
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>

      {expanded && (
        <div className="step-body">
          {children}
          {!hideStepActions && (
            <div className="step-actions">
              <button onClick={onApprove} className="btn-success-outline">
                <CheckCircle size={18} /> Approve Step
              </button>
              <button onClick={onReject} className="btn-danger-outline">
                <XCircle size={18} /> Reject Step
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

export const InfoItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="info-item">
    <div className="info-label">{label}</div>
    <div className="info-value">{value || 'N/A'}</div>
  </div>
);

/**
 * ImageCard - Displays identity verification images with authenticated fetching.
 *
 * Uses apiClient (the same axios instance as all other API calls) which provides:
 *   - Correct base URL from REACT_APP_API_URL
 *   - Authorization header via Zustand store interceptor
 *   - Automatic token refresh on 401
 *
 * The imagePath prop is a relative API path like:
 *   "/onboarding/identity-verifications/{id}/image/front"
 * apiClient prepends the baseURL automatically.
 */
export const ImageCard: React.FC<{ imagePath: string; label: string }> = ({ imagePath, label }) => {
  const [imageError, setImageError] = React.useState(false);
  const [imageSrc, setImageSrc] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [expanded, setExpanded] = React.useState(false);

  React.useEffect(() => {
    // Guard: reject missing or malformed paths before making any request.
    //
    // Valid API paths start with "/" and contain at least one path segment:
    //   /onboarding/identity-verifications/{id}/image/front
    //   /onboarding/inspection/{id}/photo/front
    //   /onboarding/documents/{id}/file
    //
    // Invalid paths (bare filenames or disk-relative paths) are rejected:
    //   "front_2026-04-05T00-54-22-288Z.jpg"
    //   "identity-verification/uuid_name/front_..."
    //   "C:\uploads\file.jpg"
    //
    // Accept any path starting with "/" or a known API prefix.
    const isValidApiPath =
      imagePath &&
      typeof imagePath === 'string' &&
      (imagePath.startsWith('/') || imagePath.startsWith('onboarding/'));

    if (!isValidApiPath) {
      if (imagePath) {
        // Invalid image path — skip silently in production
      }
      setLoading(false);
      setImageError(true);
      return;
    }

    let cancelled = false;
    let blobUrl: string | null = null;

    const fetchImage = async () => {
      try {
        setLoading(true);
        setImageError(false);

        // Use apiClient which already has auth headers and correct baseURL.
        // responseType: 'arraybuffer' so axios doesn't try to parse as JSON.
        // Append a cache-busting param so re-uploaded images are always fresh.
        const cacheBuster = `${imagePath.includes('?') ? '&' : '?'}t=${Date.now()}`;
        const response = await apiClient.get(`${imagePath}${cacheBuster}`, {
          responseType: 'arraybuffer',
        });

        if (cancelled) return;

        const contentType = response.headers['content-type'] || 'image/jpeg';
        const blob = new Blob([response.data], { type: contentType });
        blobUrl = URL.createObjectURL(blob);
        setImageSrc(blobUrl);
        setLoading(false);
      } catch (err: any) {
        if (!cancelled) {
          setImageError(true);
          setLoading(false);
        }
      }
    };

    fetchImage();

    // Cleanup blob URL on unmount to prevent memory leaks
    return () => {
      cancelled = true;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [imagePath, label]);

  const handleImageClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (imageSrc) setExpanded(true);
  };

  // Close fullscreen on Escape key, lock body scroll while overlay is open
  React.useEffect(() => {
    if (!expanded) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [expanded]);

  return (
    <>
      <div style={{
        background: '#f8fafc',
        borderRadius: '12px',
        border: '2px solid #e2e8f0',
        overflow: 'hidden',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
      >
        <div style={{
          width: '100%',
          height: '220px',
          background: '#e2e8f0',
          position: 'relative',
          cursor: imageSrc ? 'pointer' : 'default',
          overflow: 'hidden'
        }}
        onClick={handleImageClick}
        >
          {loading ? (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#94a3b8',
              gap: '8px'
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                border: '3px solid #cbd5e1',
                borderTopColor: '#3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <span style={{ fontSize: '12px' }}>Loading...</span>
            </div>
          ) : imageError || !imageSrc ? (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#94a3b8',
              gap: '8px'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="3" y1="3" x2="21" y2="21"></line>
              </svg>
              <span style={{ fontSize: '12px' }}>Image unavailable</span>
            </div>
          ) : (
            <>
              <img
                src={imageSrc}
                alt={label}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <div
                className="image-hover-overlay"
                style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  pointerEvents: 'none'
                }}
              >
                Click to enlarge
              </div>
            </>
          )}
        </div>
        <div style={{ padding: '12px', background: 'white' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>
            {label}
          </div>
        </div>
      </div>

      {/* Fullscreen overlay */}
      {expanded && imageSrc && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
          onClick={() => setExpanded(false)}
        >
          <button
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              color: 'white',
              fontSize: '24px',
              cursor: 'pointer',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '50%',
              border: 'none',
              zIndex: 10001
            }}
            onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
            aria-label="Close"
          >
            ✕
          </button>
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            color: 'white',
            fontSize: '16px',
            fontWeight: '600',
            background: 'rgba(0,0,0,0.5)',
            padding: '8px 16px',
            borderRadius: '8px'
          }}>
            {label}
          </div>
          <img
            src={imageSrc}
            alt={label}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({
  title,
  onClose,
  children
}) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h2>{title}</h2>
        <button onClick={onClose} className="modal-close">×</button>
      </div>
      <div className="modal-body">{children}</div>
    </div>
  </div>
);